import type { FC } from 'react'
import type { Theme } from '@mui/material';
import React from 'react';
import {
  Box,
  Card,
  Container,
  Grid,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { ParallaxBanner, ParallaxProvider } from 'react-scroll-parallax';
import { Helmet } from 'react-helmet';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { graphql, useStaticQuery } from 'gatsby';
import AppBar from '../components/AppBar';
import Link from '../components/Link';
import ProjectCard from '../components/ProjectCard';
import '@fortawesome/fontawesome-free/css/all.css';

const Index: FC = () => {
  const { bannerImage } = useStaticQuery(
    graphql`
      query {
        bannerImage: file(relativePath: { eq: "banner.png" }) {
          childImageSharp {
            gatsbyImageData (
              placeholder: BLURRED
              formats: [AUTO, PNG]
            )
          }
        }
      }
    `
  );
  const image = getImage(bannerImage);

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down(theme.breakpoints.values.md));

  return (
    <ParallaxProvider>
      <Helmet title="Scawwy Howwow Theatre, Inc" />
      <Box
        sx={{
          component: 'main',
          backgroundColor: 'black',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <AppBar hideProfile />
        <ParallaxBanner
          style={{ backgroundColor: 'black', height: mdDown ? '60vh' : '70vh' }}
          layers={[
            {
              children: <GatsbyImage
                style={mdDown ? { height: '30vh' } : {}}
                image={image}
                alt="Banner"
              />,
              expanded: false,
              speed: -25,
            }
          ]}
        />
        <Card
          sx={{
            width: '100%',
            borderRadius: '32px 32px 0px 0px',
            flex: '1 0 auto',
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ my: 4 }}>
              <Typography variant="h1">
                Who are we?
              </Typography>
              <Typography color="textSecondary" variant="body1">
                We're an independent theatre company that specializes in experimental work. We want to create
                new kinds of experiences and elevate new voices to push the bounds of theatre and media.{' '}
                <Link to="/about">Read more about what we do and who we are</Link>.
              </Typography>
              <Typography variant="h1" sx={{ pt: 6 }}>
                What are we currently working on?
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <ProjectCard
                    title="Wows In Pwogwess"
                    body="A monthly script workshop for screenwriters and/or playwrights presenting works in progress"
                    src="/static/wip.jpg"
                    href="/projects/wip"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ProjectCard
                    title="Found Cassettes"
                    body="A horror podcast anthology series made from writer-submitted scripts"
                    src="/static/found-cassettes.jpg"
                    href="/projects/found-cassettes"
                  />
                </Grid>
              </Grid>
              <Typography variant="h1" sx={{ pt: 6 }}>
                What have we done?
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <ProjectCard
                    title="rEMOTION 2021"
                    body="An online charity theatre festival centering on global Asian diaspora"
                    src="/static/remotion-2021.jpg"
                    href="/projects/remotion-2021"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ProjectCard
                    title="untitled [redacted]"
                    body="A surreal horror audio drama/podcast"
                    src="/static/untitled-redacted.jpg"
                    href="/projects/untitled-redacted"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ProjectCard
                    title="Tales From the Visa Line 2020"
                    body="An online charity theatre festival benefitting the International Refugee Assistance Project"
                    src="/static/tales-2020.png"
                    href="/projects/tales-2020"
                  />
                </Grid>
              </Grid>
            </Box>
          </Container>
        </Card>
      </Box>
    </ParallaxProvider>
  );
};

export default Index;
